/**
 * normalizePlacementMeta.ts — PLAT-10921, epic decision E11.
 *
 * `manuallyPositioned` ships today, works, and is persisted in every story ever
 * saved — **inside each video record's `extra_elements_json` blob**, so it is not
 * a schema field and no DatoCMS migration can reach it. It is normalized ONCE
 * here, at the boundary where `VideoRepository` already turns that stored JSON
 * into a `Video`, so every layer above speaks only the new vocabulary.
 *
 * | Legacy record                            | Normalizes to                | Why |
 * | ---------------------------------------- | ---------------------------- | --- |
 * | End card, `manuallyPositioned === true`  | `placementPinOverride: false`| It was the *creator's* own unpin, so it maps to the creator's field — which makes it revertible in PLAT-10922 |
 * | End card, flag absent or `false`         | `placementPinned: true`      | It was being reconciled. Same behaviour as before |
 * | **Anything else**                        | `placementPinned: false`     | **Nothing reconciles them today. They are frozen** |
 *
 * > **THE TRAP.** `manuallyPositioned` was only ever consulted for end cards, so
 * > every other legacy composition has no flag *and* no enforcement. Defaulting
 * > those to `placementPinned: true` would hand them to the new reconciler and
 * > **silently move graphics in stories that are already finished and
 * > published.** That is the whole risk of this ticket, and this table is the
 * > mitigation.
 *
 * **The time-override flags are never synthesized.** A legacy
 * `manuallyPositioned: true` records *that* a card was placed by hand, not
 * *which edge* moved. Inventing them would be fabricating history; with
 * `placementPinned: false` every override is allowed anyway, so behaviour is
 * identical either way.
 *
 * `manuallyPositioned` itself is **left in the blob**. It is read here and never
 * written again; rewriting stored history is not worth the risk.
 */

import { isEndCardMeta } from '@src/utility/extraElementData';

import type { Video } from '@src/types.ts/story';

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

/**
 * Adds the PLAT-10921 placement fields to any composition record that predates
 * them. Records that already carry `placementPinned` (or the creator's
 * `placementPinOverride`) are left exactly as saved — normalization is for
 * legacy data, not a chance to re-decide current data.
 *
 * Mutates and returns the same object: it is a freshly-parsed
 * `JSON.parse(extra_elements_json)` result with no other referrer yet.
 */
export function normalizePlacementMeta(
  extraElementData: Video['extraElementData'],
): Video['extraElementData'] {
  if (!isRecord(extraElementData)) return extraElementData;

  // Iterated as loose records: this is freshly-parsed JSON whose real shape is a
  // runtime question, and the legacy keys being read here are not all on the
  // `ExtraElementData` type.
  for (const meta of Object.values(
    extraElementData as Record<string, unknown>,
  )) {
    if (!isRecord(meta)) continue;
    // Already speaks the new vocabulary — written at creation or by an earlier
    // load. Nothing to normalize.
    if ('placementPinned' in meta || 'placementPinOverride' in meta) continue;
    // Not a composition at all (volume key points, punch list data, logos…).
    if (meta.type !== 'composition' && !('compositionType' in meta)) continue;

    if (isEndCardMeta(meta) && meta.manuallyPositioned === true) {
      // The creator's own unpin → the creator's field, so PLAT-10922's Revert
      // can put it back. `placementPinned` stays absent: what the template
      // authored was "pinned", and that is what Revert should return to.
      meta.placementPinOverride = false;
      continue;
    }

    // An end card that was being reconciled keeps being reconciled; everything
    // else was frozen and stays frozen.
    meta.placementPinned = isEndCardMeta(meta);
  }

  return extraElementData;
}
